import { Group, Paper, Stack, Text } from '@mantine/core';
import { ViewPanel } from '@ayocore/platform/client/components/EntityViewLayout';
import { LineChart, getFilteredChartTooltipPayload } from '@mantine/charts';
import type { PartnerChartPoint } from '@ayocore/erp/crm/server/repositories/PartnerRepository';

type ChartPoint = {
  month: string;
  pay: number;
  sell: number;
};

const formatMillions = (value: number) => {
  const rounded = Math.round((value / 1_000_000) * 100) / 100;
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
  return `${formatted} млн ₽`;
};

const formatMonth = (value: string) => {
  const [year, month] = value.split('-').map(Number);
  if (!year || !month) {
    return value;
  }
  const shortYear = String(year).slice(-2);
  return `${month.toString().padStart(2, '0')}.${shortYear}`;
};

const seriesLabels: Record<string, string> = {
  pay: 'Оплаты',
  sell: 'Отгрузки',
};

export function PartnerPaymentsShipmentsChart({ data }: { data: PartnerChartPoint[] }) {
  const chartData: ChartPoint[] = data.map(item => ({
    month: formatMonth(item.month),
    pay: item.pay_total,
    sell: item.sell_total,
  }));

  return (
    <ViewPanel>
      <Group justify='space-between' align='center' mb='xs'>
        <Text fw={400} fz={11}>
          Оплаты и отгрузки за 12 месяцев
        </Text>
        <Group gap='md'>
          <Group gap={6}>
            <Text fz={9} c='teal.6' fw={600}>
              Оплаты
            </Text>
          </Group>
          <Group gap={6}>
            <Text fz={9} c='blue.6' fw={600}>
              Отгрузки
            </Text>
          </Group>
        </Group>
      </Group>

      <LineChart
        h={220}
        data={chartData}
        dataKey='month'
        series={[
          { name: 'pay', label: 'Оплаты', color: 'teal.6' },
          { name: 'sell', label: 'Отгрузки', color: 'blue.6' },
        ]}
        curveType='natural'
        strokeWidth={3}
        withLegend={false}
        lineChartProps={{ margin: { left: 24, right: 16, top: 14 } }}
        xAxisProps={{ tick: { fontSize: 9 } }}
        yAxisProps={{ width: 60, tickMargin: 6, tick: { fontSize: 9 } }}
        valueFormatter={value => formatMillions(value)}
        tooltipProps={{
          content: ({ label, payload }) => {
            if (!payload) {
              return null;
            }
            const filtered = getFilteredChartTooltipPayload([...payload]);
            return (
              <Paper px='md' py='sm' withBorder shadow='none' radius='md'>
                <Text fw={500} mb={4}>
                  {label}
                </Text>
                <Stack gap={2}>
                  {filtered.map(item => (
                    <Text key={item.name} c={item.color} size='sm'>
                      {(item.name && seriesLabels[item.name]) ?? item.label ?? item.name}:{' '}
                      {formatMillions(Number(item.value) || 0)}
                    </Text>
                  ))}
                </Stack>
              </Paper>
            );
          },
        }}
      />
    </ViewPanel>
  );
}
